import {
  Card,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  borderRadius,
  fontWeight,
  lineHeight,
  zIndex,
} from '@verantos-dev/shared-ui-components/tokens';

import { useTableShell } from '../hooks/useTableShell';
import type { TableShellLabel, TableShellProps } from '../types';
import { TableLinkButton } from './TableLinkButton';

const DIVIDER_BOTTOM_BORDER_SX = {
  borderBottom: '1px solid',
  borderBottomColor: 'divider',
} as const;

// Pinned first column: stays visible while the remaining columns scroll horizontally.
const PINNED_CELL_SX = {
  position: 'sticky',
  left: 0,
  zIndex: zIndex.sticky,
  bgcolor: 'background.paper',
} as const;

const DIVIDER_CELL_BORDER_SX = {
  ...DIVIDER_BOTTOM_BORDER_SX,
  borderLeft: '1px solid',
  borderLeftColor: 'divider',
} as const;

const COLUMN_WIDTH_PX = 182;

const CELL_PADDING_Y = { body: 1.5, section: 2, header: 2.5 } as const;
const COMPACT_LINE_HEIGHT = `${lineHeight.normal}%`;
const COMPACT_TEXT_SX = { lineHeight: COMPACT_LINE_HEIGHT } as const;

export const TableShell = (props: TableShellProps) => {
  const { table } = props;
  const {
    title,
    characteristicLabel,
    noRowsLabel,
    columnCount,
    getSectionHeader,
    getRowLabel,
    getColumnHeader,
    getCell,
    previewAvId,
    onClosePreview,
    renderVariablePreview,
  } = useTableShell(props);

  const renderLabel = ({ label, onClick, testId }: TableShellLabel) =>
    onClick ? (
      <TableLinkButton onClick={onClick} data-testid={testId}>
        {label}
      </TableLinkButton>
    ) : (
      label
    );

  return (
    <>
      <Card
        variant="outlined"
        data-testid="analysis-table-grid"
        sx={{ borderRadius: `${borderRadius.md}px`, overflow: 'hidden' }}
      >
        <Stack direction="row" alignItems="center" sx={{ px: 4, py: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: fontWeight.semibold, alignSelf: 'stretch' }}
            noWrap
          >
            {title}
          </Typography>
        </Stack>
        <Divider />
        <TableContainer>
          <Table
            size="small"
            data-testid="analysis-table-grid-table"
            // Fixed layout gives every column with an explicit width exactly that width;
            // the unsized first column takes the remaining space. minWidth makes the
            // container scroll horizontally once the columns no longer fit.
            sx={{
              tableLayout: 'fixed',
              width: '100%',
              minWidth: COLUMN_WIDTH_PX * columnCount,
            }}
          >
            <TableHead
              sx={{
                '& .MuiTableRow-root:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <TableRow>
                <TableCell
                  sx={{
                    py: CELL_PADDING_Y.header,
                    fontWeight: fontWeight.medium,
                    ...COMPACT_TEXT_SX,
                    ...PINNED_CELL_SX,
                    ...DIVIDER_BOTTOM_BORDER_SX,
                  }}
                >
                  {characteristicLabel}
                </TableCell>
                {table.columns.map((column) => {
                  const columnHeader = getColumnHeader(column);
                  return (
                    <TableCell
                      key={column.columnId}
                      align="left"
                      sx={{
                        py: CELL_PADDING_Y.header,
                        width: COLUMN_WIDTH_PX,
                        ...DIVIDER_CELL_BORDER_SX,
                      }}
                    >
                      <Stack alignItems="flex-start">
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            fontWeight: fontWeight.medium,
                            ...COMPACT_TEXT_SX,
                          }}
                          // Blue marks a link; a header with nothing to open stays plain.
                          color={columnHeader.onClick ? 'primary' : 'text.primary'}
                        >
                          {renderLabel(columnHeader)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          sx={{ lineHeight: COMPACT_LINE_HEIGHT }}
                        >
                          {column.intent ?? ''}
                        </Typography>
                      </Stack>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody
              sx={{
                '& .MuiTableRow-root:hover': {
                  backgroundColor: 'transparent',
                },
                '& .MuiTableRow-root:last-of-type .MuiTableCell-root': {
                  borderBottom: 'none',
                },
              }}
            >
              {table.rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columnCount} sx={{ py: CELL_PADDING_Y.body }}>
                    <Typography variant="body2" color="text.secondary" sx={COMPACT_TEXT_SX}>
                      {noRowsLabel}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                table.rows.map((row, index) =>
                  row.kind === 'section_header' ? (
                    <TableRow key={`section-header-${index}`}>
                      <TableCell
                        colSpan={columnCount}
                        data-testid={`analysis-table-grid-section-${index}`}
                        sx={{
                          fontWeight: fontWeight.medium,
                          py: CELL_PADDING_Y.section,
                          ...COMPACT_TEXT_SX,
                          borderBottomColor: 'divider',
                        }}
                      >
                        {renderLabel(getSectionHeader(row))}
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={row.rowId} data-testid={`analysis-table-grid-row-${row.rowId}`}>
                      <TableCell
                        sx={{
                          py: CELL_PADDING_Y.body,
                          ...COMPACT_TEXT_SX,
                          ...PINNED_CELL_SX,
                          ...DIVIDER_BOTTOM_BORDER_SX,
                        }}
                      >
                        {renderLabel(getRowLabel(row))}
                      </TableCell>
                      {table.columns.map((column) => {
                        const { value, isPopulated } = getCell(row, column);
                        return (
                          <TableCell
                            key={column.columnId}
                            align="left"
                            data-testid={`analysis-table-grid-cell-${row.rowId}-${column.columnId}`}
                            sx={{
                              py: CELL_PADDING_Y.body,
                              color: 'text.secondary',
                              ...DIVIDER_CELL_BORDER_SX,
                            }}
                          >
                            <Typography
                              variant="body2"
                              color={isPopulated ? 'text.primary' : 'inherit'}
                              sx={{ overflowWrap: 'anywhere', ...COMPACT_TEXT_SX }}
                            >
                              {value}
                            </Typography>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  )
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      {renderVariablePreview?.({ avId: previewAvId, onClose: onClosePreview })}
    </>
  );
};
